import React, { useEffect, useState, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const PhotoCarousel = ({ docId }) => {
  const [photoData, setPhotoData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Memoize these functions so that they don't cause re-renders
  const nextImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % photoData.length);
    setSelectedImageIndex(0);
  }, [photoData.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + photoData.length) % photoData.length);
    setSelectedImageIndex(0);
  }, [photoData.length]);

  // Ensure swipeHandlers remains stable
  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextImage,
    onSwipedRight: prevImage,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
  });

  useEffect(() => {
    if (!docId) return;
    
    const fetchData = async () => {
      try {
        const reviewRef = doc(db, 'reviews', docId);
        const reviewDoc = await getDoc(reviewRef);

        if (!reviewDoc.exists()) {
          throw new Error('Document not found');
        }

        const reviewData = reviewDoc.data();
        if (reviewData.isExternal) {
          setPhotoData([]);
          return;
        }

        const progressPictures = reviewData.progress_pictures || [];
        const groups = {};

        progressPictures.forEach((photo) => {
          const parsedName = photo.name.replace(/[\[\]]/g, '').split('_');
          if (parsedName.length !== 2) return;

          const timestamp = parseInt(parsedName[0], 10);
          const order = parseInt(parsedName[1], 10);

          if (isNaN(timestamp) || isNaN(order)) return;

          if (!groups[timestamp]) groups[timestamp] = [];
          groups[timestamp].push({ url: photo.downloadURL, order });
        });

        const sortedGroups = Object.entries(groups)
          .map(([ts, photos]) => ({
            timestamp: parseInt(ts, 10),
            photos: photos.sort((a, b) => a.order - b.order),
          }))
          .sort((a, b) => a.timestamp - b.timestamp);

        setPhotoData(sortedGroups);
      } catch (error) {
        //console.error('Error loading photo data:', error);
      }
    };

    fetchData();
  }, [docId]);

  if (!photoData.length) return <div>Loading...</div>;

  // Add optional chaining for currentImages
  const currentImages = photoData[currentIndex] || {};
  const { timestamp, photos } = currentImages;
  
  // Add fallback for photos array
  const safePhotos = photos || [];

  const formatTimestamp = (days) => {
    if (days === -1) return "Pre-Procedure"; // Special case for -1
    if (days < 30) return `${days} Days Post-Procedure`;
    const months = Math.floor(days / 30);
    return `${months} Month${months !== 1 ? 's' : ''} Post-Procedure`;
  };
  

  return (
    <div className="flex flex-col items-center">
      {/* Main Image for Mobile and Swiping */}
      <div
        {...swipeHandlers}
        className="relative w-screen sm:w-full h-64 sm:h-80 flex sm:hidden"
      >
      <img
        src={photos[selectedImageIndex]?.url} // Main image based on selected index
        alt={timestamp || ''}
        className="w-full h-full object-cover shadow-lg cursor-pointer"
        onClick={() => setIsModalOpen(true)} // Enable tap to enlarge in modal
      />

        {/* Dot Indicators */}
        <div className="absolute bottom-2 w-full flex justify-center">
          {photoData.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 mx-1 rounded-full ${
                index === currentIndex ? 'bg-gray-900' : 'bg-gray-400'
              }`}
            ></div>
          ))}
        </div>
      </div>

      {/* Desktop View with Previews */}
      <div className="hidden sm:flex justify-center items-center w-full max-w-3xl mt-4">
        {/* Left Arrow */}
        <button
          onClick={prevImage}
          className="mr-4 w-10 h-10 flex items-center justify-center bg-gray-300 rounded-full shadow-lg hover:bg-gray-400"
        >
          &lt;
        </button>

        {/* Images (Left Preview, Main Image, Right Preview) */}
        <div className="flex items-center space-x-4">
          {/* Left Preview */}
          <img
            src={photoData[(currentIndex - 1 + photoData.length) % photoData.length]?.photos?.[0]?.url || ''}
            alt="Previous"
            className="w-40 h-40 object-cover opacity-75 rounded-lg cursor-pointer"
            onClick={prevImage} // Click navigates to previous group
          />

          {/* Main Image */}
          <div className="w-80 h-80">
            <img
              src={photos[selectedImageIndex]?.url} // Main image for desktop
              alt={timestamp || ''}
              className="w-full h-full object-cover shadow-lg rounded-lg cursor-pointer"
              onClick={() => setIsModalOpen(true)} // Open modal on click
            />
          </div>

          {/* Right Preview */}
          <img
            src={
              photoData[(currentIndex + 1) % photoData.length].photos[0].url
            }
            alt="Next"
            className="w-40 h-40 object-cover opacity-75 rounded-lg cursor-pointer"
            onClick={nextImage} // Click navigates to next group
          />
        </div>

        {/* Right Arrow */}
        <button
          onClick={nextImage}
          className="ml-4 w-10 h-10 flex items-center justify-center bg-gray-300 rounded-full shadow-lg hover:bg-gray-400"
        >
          &gt;
        </button>
      </div>

      {/* Miniature Previews */}
      <div className="mt-4 flex justify-center w-full">
        <div
          className="grid gap-3 max-w-sm sm:max-w-full"
          style={{ gridTemplateColumns: `repeat(${Math.min(safePhotos.length, 5)}, minmax(0, 1fr))` }}
          >
          {photos.map((photo, index) => (
            <button
              key={index}
              onClick={() => setSelectedImageIndex(index)} // Update selected image index
              className={`rounded-lg overflow-hidden ${
                index === selectedImageIndex ? 'ring-2 ring-gray-500' : ''
              }`}
            >
              <img
                src={photo.url}
                alt={`Preview ${index}`}
                className="w-full h-24 sm:h-16 object-contain"
              />
            </button>
          ))}
        </div>
      </div>

      <p className="text-l font-semibold p-6 text-gray-700">
        {formatTimestamp(currentImages.timestamp || 0)}
      </p>

      {/* Full-Size Image Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
          onClick={() => setIsModalOpen(false)} // Close modal on background click
        >
          <div
            className="relative"
            onClick={(e) => e.stopPropagation()} // Prevent modal close on inner click
          >
            <img
              src={photos[selectedImageIndex]?.url}
              alt={timestamp || ''}
              className="object-contain max-w-[600px] max-h-[600px]" // Restrict size
            />
          <button
            onClick={() => setIsModalOpen(false)} // Close modal
            className="fixed top-4 right-4 w-12 h-12 flex items-center justify-center bg-gray-800 text-white rounded-full z-50"
          >
            &times;
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoCarousel;
